<template>
  <component :is="layoutComponent" v-bind="layoutProps">
    <!-- Breadcrumb for iframe mode -->
    <div v-if="isEmbedMode" class="breadcrumb-strip">
      <span class="breadcrumb-item clickable" @click="goToScans">All Scans</span>
      <span class="breadcrumb-separator">›</span>
      <span class="breadcrumb-item current">View Grid</span>
    </div>

    <!-- Delete Confirmation Dialog -->
    <delete-confirm-dialog
      v-model="showDeleteDialog"
      :item-count="1"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />

    <!-- Share Modal -->
    <share-modal
      v-if="showShareModal"
      :visible="showShareModal"
      :scan-id="shareScanId"
      :scan-type="shareScanType"
      @close="closeShareModal"
      @copied="onShareCopied"
    />

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner">
        <svg class="spinner" width="40" height="40" viewBox="0 0 40 40">
          <circle class="path" cx="20" cy="20" r="16" fill="none" stroke-width="3"></circle>
        </svg>
      </div>
      <p>Loading grid scan data...</p>
    </div>

    <!-- No Data State - When no executions have run -->
    <div v-else-if="!isLoading && !hasData" class="no-data-container">
      <div class="no-data-card">
        <div class="no-data-icon">📊</div>
        <h2>Grid Scan Not Yet Executed</h2>
        <p>This grid scan is scheduled but hasn't run yet. The first execution will happen automatically based on your schedule settings.</p>
        
        <div class="schedule-info">
          <div class="schedule-item">
            <span class="schedule-label">Scan Frequency:</span>
            <span class="schedule-value">{{ scanData.scanFrequency || 'Not set' }}</span>
          </div>
          <div class="schedule-item">
            <span class="schedule-label">Scan Time:</span>
            <span class="schedule-value">{{ scanData.scanTime || 'Not set' }}</span>
          </div>
        </div>
        
        <div class="action-buttons">
          <button @click="goToDashboard" class="dashboard-btn">
            <i class="fas fa-external-link-alt"></i>
            Back to scan history
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content - When executions exist -->
    <div v-else class="view-grid-container" :class="{ 'with-breadcrumb': isEmbedMode }">
      <!-- Execution Timeline Navigation - Top Bar (Hide for manual scans) -->
      <transition name="slide-up">
        <div v-if="showTimeline && !isManualScan" class="timeline-navigation">
          <div class="timeline-scroll">
            <div class="timeline-track">
              <div 
                v-for="(execution, index) in filteredExecutions" 
                :key="execution.id"
                class="timeline-point" 
                :class="{ active: selectedExecution && selectedExecution.id === execution.id }"
                @click="selectExecution(execution)"
                :title="formatExecutionDate(execution.createdAt)"
              >
                <div class="timeline-dot"></div>
                <div class="timeline-label">{{ formatShortDateWithYear(execution.createdAt) }}</div>
              </div>
              <!-- Add more timeline points for demo if needed -->
              <div v-if="filteredExecutions.length === 1" v-for="n in 5" :key="'demo-' + n" class="timeline-point">
                <div class="timeline-dot"></div>
                <div class="timeline-label">--</div>
              </div>
            </div>
            <select v-model="timeRange" @change="filterExecutions" class="time-range-select">
              <option value="1month">Last 1 Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="1year">Last 1 Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
      </transition>

      <!-- Full Page Map -->
      <div ref="mapContainer" class="google-map-full" :class="{ 'timeline-hidden': !showTimeline || isManualScan }"></div>

      <!-- Loading Overlay for Execution Change -->
      <div v-if="loadingExecution" class="execution-loading-overlay">
        <div class="loading-content">
          <div class="loading-spinner">
            <svg class="spinner" width="40" height="40" viewBox="0 0 40 40">
              <circle class="path" cx="20" cy="20" r="16" fill="none" stroke-width="3"></circle>
            </svg>
          </div>
          <p>Loading execution data...</p>
        </div>
      </div>

      <!-- Loading Indicator for Pending Scan -->
      <div v-if="scanStatus === 'pending'" class="scan-status-overlay">
        <div class="status-card">
          <div class="loading-spinner">
            <svg class="spinner" width="40" height="40" viewBox="0 0 40 40">
              <circle class="path" cx="20" cy="20" r="16" fill="none" stroke-width="3"></circle>
            </svg>
          </div>
          <h3>Scan in Progress</h3>
          <p>Your scan is being processed. The map will update automatically.</p>
          <div class="progress-info">
            <span>{{ completedCount }} / {{ totalCount }} locations completed</span>
          </div>
        </div>
      </div>

      <!-- Back to My Business Button - Floating on map -->
      <button v-if="viewingBusiness && scanStatus === 'completed'" @click="switchBackToMainBusiness" class="back-to-business-btn" :style="backToBusinessBtnStyle">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.0002 15.1663H6.00016C2.38016 15.1663 0.833496 13.6197 0.833496 9.99967V5.99967C0.833496 2.37967 2.38016 0.833008 6.00016 0.833008H10.0002C13.6202 0.833008 15.1668 2.37967 15.1668 5.99967V9.99967C15.1668 13.6197 13.6202 15.1663 10.0002 15.1663ZM6.00016 1.83301C2.92683 1.83301 1.8335 2.92634 1.8335 5.99967V9.99967C1.8335 13.073 2.92683 14.1663 6.00016 14.1663H10.0002C13.0735 14.1663 14.1668 13.073 14.1668 9.99967V5.99967C14.1668 2.92634 13.0735 1.83301 10.0002 1.83301H6.00016Z" fill="#2477F6"/>
          <path d="M9.27993 10.7531H5.99993C5.7266 10.7531 5.49993 10.5265 5.49993 10.2531C5.49993 9.97982 5.7266 9.75315 5.99993 9.75315H9.27993C10.1333 9.75315 10.8333 9.05982 10.8333 8.19982C10.8333 7.33982 10.1399 6.64648 9.27993 6.64648H4.7666C4.49327 6.64648 4.2666 6.41982 4.2666 6.14648C4.2666 5.87315 4.49327 5.64648 4.7666 5.64648H9.27993C10.6866 5.64648 11.8333 6.79315 11.8333 8.19982C11.8333 9.60649 10.6866 10.7531 9.27993 10.7531Z" fill="#2477F6"/>
          <path d="M5.71346 7.67956C5.58679 7.67956 5.46012 7.63289 5.36012 7.53289L4.31346 6.48623C4.12012 6.29289 4.12012 5.97289 4.31346 5.77956L5.36012 4.73289C5.55346 4.53956 5.87345 4.53956 6.06679 4.73289C6.26012 4.92622 6.26012 5.24623 6.06679 5.43956L5.37345 6.13289L6.06679 6.82622C6.26012 7.01956 6.26012 7.33956 6.06679 7.53289C5.97345 7.62623 5.84679 7.67956 5.71346 7.67956Z" fill="#2477F6"/>
        </svg>

        Back to My Business
      </button>

      <!-- Left Sidebar Overlay - Only show when scan is completed and not embedded -->
      <div v-if="scanStatus === 'completed' && !isEmbeddedView" class="sidebar-overlay" :style="sidebarOverlayStyle">
        <!-- Business Card Block -->
        <div class="business-card">
          <!-- Desktop view - default -->
          <div class="desktop-only">
            <!-- Business Info Section with Average -->
            <div class="business-section">
              <div class="business-info">
                <h2 class="business-name">{{ businessInfo.name || 'Loading...' }}</h2>

              <div class="info-row">
                <i class="fas fa-map-marker-alt"></i>
                <span>{{ formatAddress(businessInfo) }}</span>
              </div>

              <div class="info-row">
                <i class="fas fa-calendar-alt"></i>
                <span>{{ formatDate(selectedExecution ? selectedExecution.createdAt : null) || 'Loading...' }}</span>
              </div>

              <div class="info-row">
                <i class="fas fa-th"></i>
                <span>{{ formatGridInfo() }}</span>
              </div>
            </div>

            <!-- Average Section -->
            <div class="average-section">
              <div class="section-label">OVERALL AVG</div>
              <div class="average-value">{{ overallAvgRank }}</div>
            </div>
          </div>

          <!-- Chart Section - Inside Business Card -->
          <div class="chart-section-inner">
            <div class="chart-container">
              <canvas ref="chartCanvas"></canvas>
            </div>
            <div class="stats-row">
              <div class="stat-item">
                <div class="stat-value" style="color: #3EBB80;">{{ highPercent }}%</div>
                <div class="stat-label">High</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" style="color: #f59e0b;">{{ mediumPercent }}%</div>
                <div class="stat-label">Medium</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" style="color: #FF0000;">{{ lowPercent }}%</div>
                <div class="stat-label">Low</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" style="color: #dcdbdb;">{{ outPercent }}%</div>
                <div class="stat-label">Out</div>
              </div>
            </div>
          </div>

            <!-- Keywords Section - Moved inside business card -->
            <div class="keywords-section">
            <div class="keywords-header" @click="toggleKeywords">
              <span>Keywords ({{ keywords.length }})</span>
              <i class="fas" :class="showKeywords ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>

            <div v-if="showKeywords" class="keywords-list">
              <div
                v-for="(keyword, index) in keywords"
                :key="index"
                class="keyword-item"
                :class="{ active: selectedKeyword === keyword.name }"
                @click="selectKeyword(keyword.name)"
              >
                <div class="keyword-name">{{ keyword.name }}</div>
                <div class="keyword-rank">Avg: {{ keyword.avgRank }}</div>
              </div>
              </div>
            </div>
          </div>

          <!-- Mobile view - with toggle -->
          <div class="mobile-only">
            <!-- Back to My Business Button - Inside mobile card -->
            <button v-if="viewingBusiness" @click="switchBackToMainBusiness" class="mobile-back-button">
              <i class="fas fa-arrow-left"></i>
              <span>Back to My Business</span>
            </button>

            <div class="business-header-with-toggle">
              <h2 class="business-name">{{ businessInfo.name || 'Loading...' }}</h2>
              <div class="mobile-actions">
                <!-- Mobile Toggle Arrow -->
                <button class="mobile-toggle-btn" @click="toggleMobileBusinessDetails">
                  <i :class="['fas', isBusinessDetailsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                </button>
                <!-- Three dots menu for mobile -->
                <button class="mobile-menu-dots" @click="showReportMenu = !showReportMenu">
                  <i class="fas fa-ellipsis-v"></i>
                </button>
              </div>
            </div>

            <!-- Collapsible business details for mobile -->
            <div v-show="!isBusinessDetailsCollapsed" class="mobile-business-content">
              <div class="business-info">
                <div class="info-row">
                  <i class="fas fa-map-marker-alt"></i>
                  <span>{{ formatAddress(businessInfo) }}</span>
                </div>

                <div class="info-row">
                  <i class="fas fa-calendar-alt"></i>
                  <span>{{ formatDate(selectedExecution ? selectedExecution.createdAt : null) || 'Loading...' }}</span>
                </div>

                <div class="info-row">
                  <i class="fas fa-th"></i>
                  <span>{{ formatGridInfo() }}</span>
                </div>
              </div>

              <!-- Mobile Average Section -->
              <div class="mobile-average-section">
                <div>
                  <div class="section-label">OVERALL AVG</div>
                </div>
                <div class="average-value">{{ overallAvgRank }}</div>
              </div>

              <!-- Mobile Keywords Section with clickable list -->
              <div class="mobile-keyword-section">
                <div class="keywords-header" @click="toggleKeywords">
                  <span class="section-label">KEYWORDS ({{ keywords.length }})</span>
                  <i class="fas" :class="showKeywords ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </div>

                <div v-if="showKeywords" class="mobile-keywords-list">
                  <div
                    v-for="(keyword, index) in keywords"
                    :key="index"
                    class="mobile-keyword-item"
                    :class="{ active: selectedKeyword === keyword.name }"
                    @click="selectKeyword(keyword.name)"
                  >
                    <div class="keyword-name">{{ keyword.name }}</div>
                    <div class="keyword-rank">Avg: {{ keyword.avgRank }}</div>
                  </div>
                </div>
              </div>

              <!-- Mobile Chart Section -->
              <div class="mobile-chart-section">
                <div class="chart-container">
                  <canvas ref="mobileChartCanvas" width="60" height="60"></canvas>
                </div>
                <div class="stats-row">
                  <div class="stat-item">
                    <div class="stat-value" style="color: #3EBB80;">{{ highPercent }}%</div>
                    <div class="stat-label">High</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-value" style="color: #f59e0b;">{{ mediumPercent }}%</div>
                    <div class="stat-label">Medium</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-value" style="color: #FF0000;">{{ lowPercent }}%</div>
                    <div class="stat-label">Low</div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-value" style="color: #dcdbdb;">{{ outPercent }}%</div>
                    <div class="stat-label">Out</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        
        <!-- Competitors Section - Bottom Aligned with Sidebar -->
        <div v-if="!isEmbeddedView" class="competitors-section" :class="{ 'expanded': showCompetitors, 'public-view': isPublicRoute }">
          <div class="competitors-header" @click="toggleCompetitors">
            <span>Competitors ({{ uniqueCompetitors.length }})</span>
            <i class="fas" :class="showCompetitors ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>

          <div v-if="showCompetitors" class="competitors-list" :style="{ maxHeight: competitorsMaxHeight }">
            <div v-for="(competitor, index) in uniqueCompetitors"
                 :key="index"
                 class="competitor-card"
                 :class="{ 'active-business': viewingBusiness && viewingBusiness.place_id === competitor.place_id }"
                 @click="switchToCompetitor(competitor)"
                 @mouseenter="showCompetitorMarker(competitor)"
                 @mouseleave="hideCompetitorMarker">
              <div class="competitor-header-row">
                <div class="competitor-title">
                  {{ competitor.name }}
                  <span v-if="competitor.isMainBusiness" class="you-label">(You)</span>
                </div>
                <div class="competitor-rank" :class="getRankBadgeClass(competitor.avgRank)">
                  Avg. rank {{ competitor.avgRank }}
                </div>
              </div>
              <div class="competitor-address">{{ formatCompetitorAddress(competitor) }}</div>
              <div class="competitor-rating">
                <span class="stars">
                  <i v-for="n in 5" :key="n" class="fas fa-star" :class="{ 'filled': n <= Math.floor(competitor.rating || 0) }"></i>
                </span>
                <span class="rating-value">{{ competitor.rating ? competitor.rating.toFixed(1) : '0.0' }}</span>
                <span class="review-count">({{ competitor.reviews || 0 }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    <!-- Overlay -->
    <div v-if="showMarkerPopup" class="popup-overlay" @click="closeMarkerPopup"></div>

    <!-- Marker Popup -->
    <div v-if="showMarkerPopup" class="marker-popup">
      <!-- Mobile drag handle -->
      <div class="mobile-drag-handle">
        <span></span>
      </div>
      <div class="popup-header">
        <h3>Search results for this location</h3>
        <button @click="closeMarkerPopup" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="popup-coordinates">
        Coordinates: {{ selectedMarkerData && selectedMarkerData.coordinates || '' }}
      </div>
      <div class="popup-businesses">
        <div v-for="(business, index) in (selectedMarkerData && selectedMarkerData.businesses) || []" :key="index" class="business-item">
          <div class="business-rank-badge" :class="getRankBadgeColor(business.localRank || index + 1)">
            {{ business.localRank || index + 1 }}
          </div>
          <div class="business-details">
            <div class="business-header">
              <h4 class="business-name">
                {{ business.name }}
                <span v-if="isMainBusiness(business)" class="you-badge">You</span>
              </h4>
              <div class="rank-display" :class="getRankBadgeColor(business.avgRank ? parseFloat(business.avgRank) : (business.localRank || index + 1))">
                Avg. rank {{ business.avgRank || (business.localRank || index + 1) }}
              </div>
            </div>
            <div class="business-address">{{ business.formatted_address || business.vicinity }}</div>
            <div class="business-meta">
              <div class="rating-section">
                <span class="rating">{{ business.rating || 0 }}</span>
                <span class="stars">
                  <i v-for="n in 5" :key="n" class="fas fa-star" :class="{ 'filled': n <= Math.floor(business.rating || 0) }"></i>
                </span>
                <span class="reviews">({{ business.user_ratings_total || 0 }})</span>
              </div>
              <!-- <button class="scan-btn">SCAN</button> -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Right Controls -->
    <div class="top-controls" :style="topControlsStyle">
      <!-- VIEW REPORT Button with Menu -->
      <div v-if="!isPublicRoute" class="report-button-container">
        <button class="view-report-btn" @click="viewReport">
          <i class="fas fa-chart-bar"></i>
          VIEW REPORT
        </button>
        <button class="menu-dots-btn" @click="showReportMenu = !showReportMenu">
          <i class="fas fa-ellipsis-v"></i>
        </button>

        <!-- Dropdown Menu -->
        <div v-if="showReportMenu" class="report-dropdown-menu" v-click-outside="() => showReportMenu = false">
          <div class="menu-item" @click="editScan">
            <i class="mdi mdi-pencil-outline"></i>
            <span>Edit</span>
          </div>
          <div class="menu-item" @click="duplicateScan">
            <i class="mdi mdi-content-copy"></i>
            <span>Duplicate</span>
          </div>
          <div class="menu-item" @click="shareScan">
            <i class="mdi mdi-share-variant-outline"></i>
            <span>Share</span>
          </div>
          <div class="menu-item" @click="exportPNG">
            <i class="mdi mdi-file-image-outline"></i>
            <span>Export as PNG</span>
          </div>
          <div class="menu-item" @click="exportCSV">
            <i class="mdi mdi-file-delimited-outline"></i>
            <span>Export as CSV</span>
          </div>
          <div class="menu-item delete" @click="deleteScan">
            <i class="mdi mdi-delete-outline"></i>
            <span>Delete</span>
          </div>
        </div>
      </div>

      <!-- Timeline Toggle Button (Hide for manual scans) -->
      <button v-if="!isManualScan" class="timeline-toggle-btn" @click="showTimeline = !showTimeline">
        <i class="fas" :class="showTimeline ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </button>
    </div>
  </div>
  </component>
</template>

<script>
import axios from 'axios'
import Chart from 'chart.js'
import PanelNavigation from '@/components/PanelNavigation.vue'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog.vue'
import ShareModal from '@/components/ShareModal.vue'
import embedMode from '@/mixins/embedMode'

export default {
  name: 'RecurringGrid',
  mixins: [embedMode],
  components: {
    PanelNavigation,
    DeleteConfirmDialog,
    ShareModal
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  },
  metaInfo() {
    return {
      title: "Recurring Grid | " + this.$appName
    }
  },
  computed: {
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    },
    isPublicRoute() {
      // Check if the route ends with /share or /embedded
      const path = this.$route.path;
      return path.endsWith('/share') || path.endsWith('/embedded');
    },
    isEmbeddedView() {
      // Check specifically for embedded mode
      return this.$route.params.mode === 'embedded';
    },
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    },
    embedToken() {
      return this.$store.getters['embed/embedToken'];
    },
    layoutComponent() {
      // Use div for public routes or embed mode, panel-navigation for authenticated routes
      return (this.isPublicRoute || this.isEmbedMode) ? 'div' : 'panel-navigation';
    },
    layoutProps() {
      // Add embed-container class for embed mode
      if (this.isEmbedMode) {
        return { class: 'embed-container' };
      }
      // Pass props for panel-navigation
      return (this.isPublicRoute) ? {} : { 'default-mini': true, 'mobileEnabled': true };
    },
    completedCount() {
      return this.processItems.filter(item => item.status === 'completed').length
    },
    totalCount() {
      return this.processItems.length
    },
    isManualScan() {
      // Check if scan frequency is manual
      // Check both scanFrequency and frequency fields
      if (!this.scanData) return false
      const frequency = this.scanData.scanFrequency || this.scanData.frequency
      return frequency && frequency.toLowerCase() === 'manual'
    },
    competitorsMaxHeight() {
      // Calculate max height for competitors list to expand upward
      // The competitors section is at bottom: 0px and should expand up
      // It should fill the space up to where the sidebar-overlay ends
      //
      // More generous calculation:
      // - Sidebar typical height: ~350-400px from top
      // - Add some gap: 20px
      // - Competitors header: ~50px
      //
      // Max height for the list content itself:
      // In embed mode, allow list to expand more
      if (this.isEmbedMode) {
        return 'calc(100vh - 380px)';
      }
      // Default calculation for regular mode
      return 'calc(100vh - 420px)';
    },
    backToBusinessBtnStyle() {
      return {
        top: (this.showTimeline && !this.isManualScan) ? '90px' : '20px'
      };
    },
    sidebarOverlayStyle() {
      return {
        top: (this.showTimeline && !this.isManualScan) ? '80px' : '20px',
        maxHeight: (this.showTimeline && !this.isManualScan) ? 'calc(100vh - 100px)' : 'calc(100vh - 40px)'
      };
    },
    topControlsStyle() {
      return {
        top: (this.showTimeline && !this.isManualScan) ? '70px' : '20px'
      };
    }
  },
  data() {
    return {
      isLoading: true,
      // Delete dialog
      showDeleteDialog: false,
      deleting: false,
      deleteIds: [],
      // Share modal
      showShareModal: false,
      shareScanId: null,
      shareScanType: 'grid_scan',
      hasData: false,
      loadingExecution: false, // Separate loading state for execution switching
      showTimeline: true, // Toggle state for timeline
      showReportMenu: false, // Toggle state for report menu
      scanData: {},
      businessInfo: {},
      googleMapsApiKey: null,
      executions: [],
      filteredExecutions: [],
      selectedExecution: null,
      timeRange: '1month',
      
      // Keywords functionality
      keywords: [],
      selectedKeyword: null,
      showKeywords: false,
      
      // Map and markers
      map: null,
      markers: [],
      gridRectangle: null,
      gridCircle: null,
      hoverMarker: null,
      
      // Performance metrics
      processItems: [],
      highCount: 0,
      mediumCount: 0,
      lowCount: 0,
      outCount: 0,
      highPercent: 0,
      mediumPercent: 0,
      lowPercent: 0,
      outPercent: 0,
      overallAvgRank: '0',
      avgRank: '0',
      
      // Chart
      chart: null,
      
      // Competitors
      uniqueCompetitors: [],
      showCompetitors: false,
      viewingBusiness: null, // Track which business we're viewing
      originalBusinessInfo: null, // Store original business info
      
      // Popup
      selectedMarkerData: null,
      showMarkerPopup: false,
      
      // Status
      scanStatus: 'completed',
      pollingInterval: null,

      // Mobile toggle state - collapsed by default on mobile
      isBusinessDetailsCollapsed: true
    }
  },
  async mounted() {
    // Track embed usage if in embedded mode
    if (this.isEmbedMode) {
      this.trackEmbedUsage('recurring-grid');
    }

    await this.fetchScanData()

    // For non-public routes, ensure API key is loaded from store
    if (!this.isPublicRoute && !this.$store.state.apiKeys.googleMaps.apiKey) {
      await this.$store.dispatch('apiKeys/fetchGoogleMapsKey');
    }

    if (this.hasData) {
      this.$nextTick(() => {
        this.loadGoogleMaps()
        this.createChart()
      })
    }
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }
    if (this.markers) {
      this.markers.forEach(marker => marker.setMap(null))
    }
    if (this.hoverMarker) {
      this.hoverMarker.setMap(null)
    }
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (e) {
        // Ignore destroy errors on cleanup
      }
      this.chart = null;
    }
  },
  methods: {
    goToScans() {
      this.$router.push('/scans');
    },
    toggleMobileBusinessDetails() {
      this.isBusinessDetailsCollapsed = !this.isBusinessDetailsCollapsed;

      // If expanding and on mobile, ensure chart is created
      if (!this.isBusinessDetailsCollapsed && this.$vuetify.breakpoint.smAndDown && this.scanStatus === 'completed') {
        this.$nextTick(() => {
          setTimeout(() => {
            try {
              // Force chart recreation for mobile
              if (this.chart) {
                this.chart.destroy();
                this.chart = null;
              }
            } catch (e) {
              // If destroy fails, just set to null
              this.chart = null;
            }
            this.createChart();
          }, 150);
        });
      }
    },


    addHomeControl(center) {
      // Create the home control button
      const homeControlDiv = document.createElement('div');

      // Set CSS for the control border
      const controlUI = document.createElement('div');
      controlUI.style.backgroundColor = '#fff';
      controlUI.style.border = '2px solid #fff';
      controlUI.style.borderRadius = '3px';
      controlUI.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
      controlUI.style.cursor = 'pointer';
      controlUI.style.marginRight = '10px';
      controlUI.style.marginTop = '10px';
      controlUI.style.textAlign = 'center';
      controlUI.title = 'Click to recenter the map';
      homeControlDiv.appendChild(controlUI);

      // Set CSS for the control interior
      const controlText = document.createElement('div');
      controlText.style.color = 'rgb(25,25,25)';
      controlText.style.fontFamily = 'Roboto,Arial,sans-serif';
      controlText.style.fontSize = '16px';
      controlText.style.lineHeight = '38px';
      controlText.style.paddingLeft = '12px';
      controlText.style.paddingRight = '12px';
      controlText.innerHTML = '⌂';
      controlUI.appendChild(controlText);

      // Setup the click event listeners
      controlUI.addEventListener('click', () => {
        this.map.setCenter(center);
        this.map.setZoom(14);
      });

      homeControlDiv.index = 1;
      this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(homeControlDiv);
    },

    formatAddress(businessInfo) {
      if (!businessInfo) return 'Loading...';

      // If we have a full address, use it
      if (businessInfo.address) {
        return businessInfo.address;
      }

      // Build address from components
      const parts = [];
      if (businessInfo.city) parts.push(businessInfo.city);
      if (businessInfo.state) parts.push(businessInfo.state);
      if (businessInfo.postal_code) parts.push(businessInfo.postal_code);
      if (businessInfo.country) parts.push(businessInfo.country);

      // If we have any parts, join them
      if (parts.length > 0) {
        return parts.join(', ');
      }

      // Default fallback
      return businessInfo.country || 'Service Area Business';
    },

    formatCompetitorAddress(competitor) {
      if (!competitor) return '';

      // If we have a full address, use it
      if (competitor.address) {
        return competitor.address;
      }

      // Try to get from business_data
      if (competitor.business_data) {
        const data = typeof competitor.business_data === 'string'
          ? JSON.parse(competitor.business_data)
          : competitor.business_data;

        if (data.address) return data.address;

        // Build from components
        const parts = [];
        if (data.city) parts.push(data.city);
        if (data.state) parts.push(data.state);
        if (data.postal_code) parts.push(data.postal_code);
        if (data.country) parts.push(data.country);

        if (parts.length > 0) {
          return parts.join(', ');
        }

        return data.country || 'Service Area Business';
      }

      return 'Service Area Business';
    },

    async fetchScanData() {
      try {
        const scanId = this.$route.params.id
        
        // First fetch basic scan info
        const scanResponse = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan-details/${scanId}`
        )

        // Store the Google Maps API key if provided
        if (scanResponse.data.googleMapsApiKey) {
          this.googleMapsApiKey = scanResponse.data.googleMapsApiKey;
        }

        if (scanResponse.data.success) {
          this.scanData = scanResponse.data.data


          // Store gridSize in scanData with correct field name
          if (this.scanData.gridSize && !this.scanData.grid_size) {
            this.scanData.grid_size = this.scanData.gridSize
          }

          this.businessInfo = this.scanData.business ?
            (typeof this.scanData.business === 'string' ? JSON.parse(this.scanData.business) : this.scanData.business) : {}

          // Ensure businessInfo has grid_size
          if (!this.businessInfo.grid_size && this.scanData.grid_size) {
            this.businessInfo.grid_size = this.scanData.grid_size
          }

          // Check if there's execution data
          this.hasData = scanResponse.data.data.hasData || false
          
          if (this.hasData) {
            // Fetch executions if data exists
            await this.fetchExecutions()
          }
        }
      } catch (error) {
        console.error('Error fetching scan data:', error)
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load scan data'
        })
      } finally {
        this.isLoading = false
      }
    },

    async fetchExecutions() {
      try {
        const scanId = this.$route.params.id
        
        // Fetch all executions for this scan
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${scanId}/executions`,
          {
            params: {
              status: 'completed', // Only get completed executions
              limit: 50
            }
          }
        )

        // Store the Google Maps API key if provided
        if (response.data.googleMapsApiKey) {
          this.googleMapsApiKey = response.data.googleMapsApiKey;
        }

        if (response.data.success) {
          const { executions, scan } = response.data.data
          
          // Store executions - they come from backend in DESC order (newest first)
          // We'll reverse them for display (oldest left, newest right)
          this.executions = executions.map(exec => ({
            id: exec.id,
            createdAt: exec.execution_date || exec.createdAt,
            status: exec.status,
            totalItems: exec.total_items,
            completedItems: exec.completed_items
          }))
          
          // Parse business info from scan
          if (scan.business) {
            this.businessInfo = typeof scan.business === 'string' ? 
              JSON.parse(scan.business) : scan.business
          }
          
          this.filterExecutions()
          
          // Select the latest execution by default (which is the last one after reversing)
          if (this.filteredExecutions.length > 0) {
            // The latest execution is the last one in the filtered list (after reversing)
            const latestExecution = this.filteredExecutions[this.filteredExecutions.length - 1]
            await this.selectExecution(latestExecution)
          }
        }
      } catch (error) {
        console.error('Error fetching executions:', error)
      }
    },

    async selectExecution(execution) {
      try {
        // Validate execution object
        if (!execution || !execution.id) {
          console.error('Invalid execution object:', execution)
          this.loadingExecution = false
          return
        }

        this.selectedExecution = execution
        this.loadingExecution = true // Show loading indicator

        // Fetch the specific execution data
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${this.$route.params.id}/execution/${execution.id}`
        )

        // Store the Google Maps API key if provided
        if (response.data.googleMapsApiKey) {
          this.googleMapsApiKey = response.data.googleMapsApiKey;
        }
        
        if (response.data.success) {
          const { execution: execData, scan, processItems, scanItems } = response.data.data

          // Store process items
          this.processItems = processItems || []

          // Store grid_size from scan data if available
          if (scan.grid_size) {
            this.scanData.grid_size = scan.grid_size

          }

          // Update business info if needed
          if (scan.business && !this.businessInfo.name) {
            this.businessInfo = scan.business
          }

          // Ensure businessInfo has grid_size
          if (!this.businessInfo.grid_size && scan.grid_size) {
            this.businessInfo.grid_size = scan.grid_size
          }
          
          // Check scan status based on execution status
          this.scanStatus = execData.status === 'completed' ? 'completed' : 'pending'
          
          // If status is pending, check if all process items are completed
          if (this.scanStatus === 'pending') {
            const allCompleted = this.processItems.every(item => item.status === 'completed')
            if (allCompleted && this.processItems.length > 0) {
              this.scanStatus = 'completed'
            }
          }
          
          // Extract keywords and calculate stats
          this.extractKeywords()
          this.calculateMetrics()
          this.extractCompetitors()
          
          // Update chart and map in the next tick
          this.$nextTick(() => {
            // Update or create chart
            if (this.chart) {
              this.updateChart()
            } else if (this.$refs.chartCanvas) {
              this.createChart()
            }

            // Update map markers without reinitializing the map
            if (this.map) {
              // Just update markers, don't reinitialize the whole map
              this.clearMarkers()
              this.updateMapMarkers()
              // Only fit bounds if we have items
              if (this.processItems.length > 0) {
                this.fitMapToGrid()
              }
            } else {
              console.warn('Map not yet initialized after selecting execution')
            }

            // Hide loading indicator after all updates are done
            this.loadingExecution = false
          })
        }
      } catch (error) {
        console.error('Error fetching execution data:', error)
        this.loadingExecution = false // Hide loading indicator on error
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load execution data'
        })
      }
    },

    extractKeywords() {
      const keywordMap = new Map()

      // Get total grid locations from grid_size
      const gridSize = this.scanData.grid_size || '3x3'
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3)
      const totalGridLocations = rows * cols

      // First pass: collect all keywords
      this.processItems.forEach(item => {
        if (item.keyword) {
          if (!keywordMap.has(item.keyword)) {
            keywordMap.set(item.keyword, {
              name: item.keyword,
              totalRank: 0,
              completedCount: 0,
              totalCount: 0
            })
          }

          const keywordData = keywordMap.get(item.keyword)
          keywordData.totalCount++

          if (item.status === 'completed') {
            keywordData.completedCount++
            // For average calculation, treat rank 0 as 20
            const actualRank = parseInt(item.rank || 0)
            const rankForAvg = (actualRank === 0) ? 20 : actualRank
            keywordData.totalRank += rankForAvg
          }
        }
      })

      // Calculate averages for each keyword
      this.keywords = Array.from(keywordMap.values()).map(keyword => {
        // Add rank 20 for missing locations for this keyword
        const missingLocations = totalGridLocations - keyword.completedCount
        let totalRankWithMissing = keyword.totalRank
        if (missingLocations > 0) {
          totalRankWithMissing += missingLocations * 20
        }

        return {
          name: keyword.name,
          avgRank: (totalRankWithMissing / totalGridLocations).toFixed(1),
          count: keyword.totalCount
        }
      })

      // Select first keyword by default if none selected
      if (this.keywords.length > 0 && !this.selectedKeyword) {
        this.selectedKeyword = this.keywords[0].name
      }
    },

    selectKeyword(keywordName) {
      this.selectedKeyword = keywordName
      this.calculateMetrics()
      this.extractCompetitors()
      
      // Ensure map is initialized before updating markers
      if (this.map) {
        this.updateMapMarkers()
      } else {
        console.warn('Map not initialized when selecting keyword')
      }
      
      if (this.chart) {
        this.updateChart()
      }
    },

    calculateMetrics() {
      // Get total grid locations from grid_size
      const gridSize = this.scanData.grid_size || '3x3'
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3)
      const totalGridLocations = rows * cols

      // If no keyword is selected, use the first one
      if (!this.selectedKeyword && this.keywords.length > 0) {
        this.selectedKeyword = this.keywords[0].name
      }

      // Filter process items by selected keyword
      const keywordItems = this.selectedKeyword ?
        this.processItems.filter(item => item.keyword === this.selectedKeyword) :
        []

      let high = 0, medium = 0, low = 0, out = 0
      let totalRank = 0
      let completedCount = 0

      keywordItems.forEach(item => {
        if (item.status === 'completed') {
          completedCount++

          // Get the actual rank value
          const actualRank = parseInt(item.rank || 0)

          // For average calculation only, treat rank 0 as 20
          const rankForAvg = (actualRank === 0) ? 20 : actualRank
          totalRank += rankForAvg

          // For categorization, use actual rank
          if (actualRank > 0 && actualRank <= 20) {
            if (actualRank <= 5) {
              high++
            } else if (actualRank <= 14) {
              medium++
            } else {
              low++
            }
          } else {
            out++
          }
        }
      })

      // Add rank 20 for any missing locations
      const missingLocations = totalGridLocations - completedCount
      if (missingLocations > 0) {
        totalRank += missingLocations * 20
        out += missingLocations
      }

      this.highCount = high
      this.mediumCount = medium
      this.lowCount = low
      this.outCount = out

      const total = totalGridLocations || 1
      this.highPercent = Math.round((high / total) * 100)
      this.mediumPercent = Math.round((medium / total) * 100)
      this.lowPercent = Math.round((low / total) * 100)
      this.outPercent = Math.round((out / total) * 100)

      // Calculate average for the selected keyword
      this.avgRank = (totalRank / total).toFixed(2)

      // The overall average shown should be for the selected keyword only
      this.overallAvgRank = this.avgRank
    },

    extractCompetitors() {
      const competitorsMap = new Map()
      const mainBusinessPlaceId = this.businessInfo.place_id || this.businessInfo.google_place_id
      const mainBusinessName = this.businessInfo.business_name

      // Get grid size for average calculation
      const gridSize = this.businessInfo.grid_size || '3x3'
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3)
      const totalGridLocations = rows * cols

      // Filter by selected keyword
      const keywordItems = this.selectedKeyword ?
        this.processItems.filter(item => item.keyword === this.selectedKeyword) :
        this.processItems

      keywordItems.forEach(item => {
        if (item.result) {
          let results = item.result
          if (typeof results === 'string') {
            try {
              results = JSON.parse(results)
            } catch (e) {
              return
            }
          }

          if (Array.isArray(results)) {
            results.forEach((business, index) => {
              const placeId = business.place_id
              if (placeId && !competitorsMap.has(placeId)) {
                const isMainBusiness = (mainBusinessPlaceId && placeId === mainBusinessPlaceId) ||
                                      (mainBusinessName && business.name &&
                                       business.name.toLowerCase() === mainBusinessName.toLowerCase())

                // Extract coordinates - handle both possible data structures
                let lat = null, lng = null
                if (business.geometry && business.geometry.location) {
                  lat = business.geometry.location.lat
                  lng = business.geometry.location.lng
                } else if (business.lat && business.lng) {
                  // Sometimes coordinates are directly on the business object
                  lat = business.lat
                  lng = business.lng
                } else if (business.latitude && business.longitude) {
                  // Or as latitude/longitude properties
                  lat = business.latitude
                  lng = business.longitude
                }

                competitorsMap.set(placeId, {
                  name: business.name || 'Unknown Business',
                  address: business.formatted_address || business.vicinity || 'Address not available',
                  rating: business.rating || null,
                  reviews: business.user_ratings_total || 0,
                  place_id: placeId,
                  rankSum: index + 1,  // Start with first rank
                  rankCount: 1,  // Count appearances
                  bestRank: index + 1,  // Track best rank separately
                  isMainBusiness: isMainBusiness,
                  latitude: lat,
                  longitude: lng
                })
              } else if (placeId && competitorsMap.has(placeId)) {
                const existing = competitorsMap.get(placeId)
                // Add to rank sum for average calculation
                existing.rankSum += (index + 1)
                existing.rankCount += 1
                // Update to best (lowest) rank across all grid points
                if (index + 1 < existing.bestRank) {
                  existing.bestRank = index + 1
                }
              }
            })
          }
        }
      })

      // Calculate average rank for each competitor
      this.uniqueCompetitors = Array.from(competitorsMap.values())
        .map(competitor => {
          // For businesses not found in some grid points, treat as rank 20
          const missingCount = totalGridLocations - competitor.rankCount
          const totalRankSum = competitor.rankSum + (missingCount * 20)
          const avgRank = totalRankSum / totalGridLocations

          return {
            ...competitor,
            avgRank: avgRank.toFixed(2),
            bestRank: competitor.bestRank // Keep the actual best rank
          }
        })
        .sort((a, b) => parseFloat(a.avgRank) - parseFloat(b.avgRank))
    },

    filterExecutions() {
      const now = new Date()
      let cutoffDate
      
      switch (this.timeRange) {
        case '1month':
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
          break
        case '3months':
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
          break
        case '1year':
          cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
          break
        default:
          cutoffDate = new Date(0) // All time
      }
      
      // Filter executions and then reverse to show oldest first
      this.filteredExecutions = this.executions
        .filter(execution => new Date(execution.createdAt) >= cutoffDate)
        .reverse() // Reverse to show oldest on left, newest on right
    },

    formatExecutionDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatShortDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      })
    },
    
    formatShortDateWithYear(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
    },

    formatDate(dateString) {
      if (!dateString) return '0'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      })
    },

    formatGridInfo() {
      const gridSize = this.businessInfo.grid_size || '3x3'
      const radius = this.businessInfo.radius || 1
      const unit = this.businessInfo.radius_unit || 'mi'
      return `${gridSize} Grid (${this.totalCount} locations) - ${radius}${unit} distance between pins`
    },

    formatKeywords(keywords) {
      if (!keywords || keywords.length === 0) return 'No keywords'
      if (typeof keywords === 'string') return keywords
      return keywords.join(', ')
    },

    goToDashboard() {
      // In iframe mode, navigate within the same window
      // Otherwise, open in new tab for better UX
      if (this.isEmbedMode) {
        this.$router.push('/scans');
      } else {
        window.open('/scans', '_blank');
      }
    },

    loadGoogleMaps() {
      if (window.google && window.google.maps) {
        this.initMap()
        return
      }

      const script = document.createElement('script')
      // Use API key from backend response, then store, then env
      const apiKey = this.googleMapsApiKey ||
                    this.$store.getters['apiKeys/googleMapsKey'] ||
                    process.env.VUE_APP_GOOGLE_MAPS_API_KEY

      if (!apiKey) {
        console.error('No Google Maps API key configured')
        return
      }

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initRecurringGridMap`
      script.async = true
      script.defer = true
      
      window.initRecurringGridMap = () => {
        this.initMap()
      }
      
      document.head.appendChild(script)
    },

    initMap() {
      // Initialize retry counter if not exists
      if (!this.mapInitRetries) {
        this.mapInitRetries = 0;
      }

      // Check if mapContainer ref exists
      if (!this.$refs.mapContainer) {
        this.mapInitRetries++;

        // Stop retrying after 10 attempts (1 second total)
        if (this.mapInitRetries > 10) {
          console.error('Map container not found after 10 retries. Stopping.');
          return;
        }

       
        // Retry after DOM updates
        this.$nextTick(() => {
          if (this.$refs.mapContainer) {
            this.mapInitRetries = 0; // Reset counter on success
            this.initMap();
          } else {
            setTimeout(() => this.initMap(), 100);
          }
        });
        return;
      }

      // Reset retry counter on successful initialization
      this.mapInitRetries = 0;

      const mapCenter = this.getGridCenter()

      this.map = new google.maps.Map(this.$refs.mapContainer, {
        zoom: 14,
        center: mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER
        },
        mapTypeControl: false, // Disabled map type control
        scaleControl: true,
        streetViewControl: false, // Disabled street view control (Pegman)
        rotateControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      })

      // Add custom home/center control
      this.addHomeControl(mapCenter)

      this.updateMapContent()
    },

    updateMapContent() {
      if (!this.map) {
        console.warn('Map not initialized in updateMapContent')
        return
      }
      
      // Clear existing markers first
      this.clearMarkers()
      
      // Redraw grid
      this.drawGrid()
      
      // Update markers with current data
      this.updateMapMarkers()
      
      // Fit map to show all markers
      if (this.processItems.length > 0) {
        this.fitMapToGrid()
      }
    },

    updateMapMarkers() {
      if (!this.map) {
        console.warn('Map not initialized in updateMapMarkers')
        return
      }
      
      // Clear existing markers
      this.clearMarkers()
      
      // Filter process items by selected keyword
      const keywordItems = this.selectedKeyword ? 
        this.processItems.filter(item => item.keyword === this.selectedKeyword) : 
        this.processItems
      
      
      keywordItems.forEach((item, index) => {
        if (item.latitude && item.longitude) {
          const position = new google.maps.LatLng(parseFloat(item.latitude), parseFloat(item.longitude))
          const rank = item.rank || 0
          const isCompleted = item.status === 'completed'
          
          let displayText = '?'
          if (isCompleted) {
            displayText = rank === 0 || rank > 20 ? '20+' : rank.toString()
          }
          
          const color = isCompleted ? this.getRankColor(rank) : '#dcdbdb' // Gray for loading
          
          const marker = new google.maps.Marker({
            position: position,
            map: this.map,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(color, displayText))}`,
              scaledSize: new google.maps.Size(32, 48),
              anchor: new google.maps.Point(16, 48)
            },
            title: `Location ${index + 1}: Rank ${rank || 'Not Ranked'}`
          })
          
          marker.addListener('click', () => {
            this.showMarkerDetails(item, position)
          })
          
          this.markers.push(marker)
        }
      })
      
      
    },

    getGridCenter() {
      if (this.processItems.length === 0) {
        return { lat: 28.5887354, lng: 77.0829215 }
      }
      
      let sumLat = 0, sumLng = 0, count = 0
      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          sumLat += parseFloat(item.latitude)
          sumLng += parseFloat(item.longitude)
          count++
        }
      })
      
      if (count === 0) {
        return { lat: 28.5887354, lng: 77.0829215 }
      }
      
      return { 
        lat: sumLat / count, 
        lng: sumLng / count 
      }
    },

    drawGrid() {
      // Clear existing grid
      if (this.gridRectangle) {
        this.gridRectangle.setMap(null)
        this.gridRectangle = null
      }
      if (this.gridCircle) {
        this.gridCircle.setMap(null)
        this.gridCircle = null
      }

      if (this.processItems.length === 0) return

      const center = this.getGridCenter()
      const gridType = this.businessInfo.grid_shape || this.businessInfo.gridType || 'square'
      const radius = this.calculateGridRadius()

      if (gridType === 'circle' || gridType === 'circular') {
        this.gridCircle = new google.maps.Circle({
          center: center,
          radius: radius,
          fillColor: '#2477f6',
          fillOpacity: 0.05,
          strokeColor: '#2477f6',
          strokeOpacity: 0.8,
          strokeWeight: 2.5,
          map: this.map
        })
      } else {
        const bounds = this.calculateSquareBounds(center, radius)
        
        this.gridRectangle = new google.maps.Rectangle({
          bounds: bounds,
          fillColor: '#2477f6',
          fillOpacity: 0.05,
          strokeColor: '#2477f6',
          strokeOpacity: 0.8,
          strokeWeight: 2.5,
          map: this.map
        })
      }
    },

    calculateGridRadius() {
      if (this.processItems.length < 2) {
        return 1000 // Default 1km
      }
      
      const center = this.getGridCenter()
      let maxDistance = 0
      
      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          const distance = this.getDistanceFromLatLonInMeters(
            center.lat, center.lng,
            parseFloat(item.latitude), parseFloat(item.longitude)
          )
          if (distance > maxDistance) {
            maxDistance = distance
          }
        }
      })
      
      return maxDistance
    },

    calculateSquareBounds(center, radiusInMeters) {
      if (this.processItems.length > 0) {
        let minLat = Infinity, maxLat = -Infinity
        let minLng = Infinity, maxLng = -Infinity
        
        this.processItems.forEach(item => {
          if (item.latitude && item.longitude) {
            const lat = parseFloat(item.latitude)
            const lng = parseFloat(item.longitude)
            
            if (lat < minLat) minLat = lat
            if (lat > maxLat) maxLat = lat
            if (lng < minLng) minLng = lng
            if (lng > maxLng) maxLng = lng
          }
        })
        
        if (minLat !== Infinity) {
          return {
            north: maxLat,
            south: minLat,
            east: maxLng,
            west: minLng
          }
        }
      }
      
      // Fallback calculation
      const latOffset = (radiusInMeters / 111320)
      const lngOffset = (radiusInMeters / (111320 * Math.cos(center.lat * Math.PI / 180)))
      
      return {
        north: center.lat + latOffset,
        south: center.lat - latOffset,
        east: center.lng + lngOffset,
        west: center.lng - lngOffset
      }
    },

    getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
      const R = 6371000
      const dLat = this.deg2rad(lat2 - lat1)
      const dLon = this.deg2rad(lon2 - lon1)
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
      return R * c
    },

    deg2rad(deg) {
      return deg * (Math.PI/180)
    },

    createMarkerSVG(color, text) {
      return `
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M16 0C7.16 0 0 7.16 0 16c0 11.2 14.4 30.4 15.36 31.52c0.16 0.16 0.4 0.32 0.64 0.32s0.48-0.16 0.64-0.32C17.6 46.4 32 27.2 32 16c0-8.84-7.16-16-16-16z" fill="${color}" stroke="#888888" stroke-width="1"/>
            <text x="16" y="21" text-anchor="middle" fill="#FFFFFF" font-size="13" font-weight="600" font-family="Arial" stroke="#333333" stroke-width="2" paint-order="stroke fill">${text}</text>
          </g>
        </svg>
      `
    },

    getRankColor(rank) {
      if (!rank || rank === 0 || rank > 20) return '#dcdbdb'
      if (rank <= 5) return '#3EBB80' // Green for 1-5
      if (rank <= 14) return '#f59e0b' // Orange for 6-14
      if (rank <= 20) return '#FF0000' // Red for 15-20
      return '#dcdbdb'
    },

    getRankBadgeClass(rank) {
      const numRank = typeof rank === 'string' ? parseFloat(rank) : rank
      if (numRank <= 3) return 'rank-high'
      if (numRank <= 10) return 'rank-medium'
      return 'rank-low'
    },

    getRankBadgeColor(rank) {
      if (rank === 1) return 'rank-top'
      if (rank <= 3) return 'rank-high'
      if (rank <= 10) return 'rank-medium'
      return 'rank-low'
    },

    formatRankDisplay(rank) {
      if (rank === 1) return 'Top'
      if (rank <= 3) return 'High'
      if (rank <= 10) return 'Medium'
      return 'Low'
    },

    clearMarkers() {
      if (this.markers && this.markers.length > 0) {
        this.markers.forEach(marker => {
          if (marker) {
            marker.setMap(null)
          }
        })
        this.markers = []
      }
    },

    fitMapToGrid() {
      if (!this.map || this.processItems.length === 0) return

      const bounds = new google.maps.LatLngBounds()
      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          bounds.extend(new google.maps.LatLng(parseFloat(item.latitude), parseFloat(item.longitude)))
        }
      })

      this.map.fitBounds(bounds)
      
      const listener = google.maps.event.addListener(this.map, "idle", () => {
        if (this.map.getZoom() > 16) this.map.setZoom(16)
        google.maps.event.removeListener(listener)
      })
    },

    createChart() {
      // Check for both desktop and mobile chart canvas
      // Use mobile canvas on mobile devices, desktop canvas on desktop
      const isMobile = this.$vuetify.breakpoint.smAndDown;
      const canvasRef = isMobile ? this.$refs.mobileChartCanvas : this.$refs.chartCanvas;

      if (!canvasRef) {
        console.log('Canvas ref not found, mobile:', isMobile);
        return;
      }

      try {
        const ctx = canvasRef.getContext('2d');

        // Safely destroy existing chart
        if (this.chart) {
          try {
            this.chart.destroy();
          } catch (e) {
            console.log('Chart destroy error:', e);
          }
          this.chart = null;
        }

        // Ensure canvas has dimensions
        if (isMobile) {
          canvasRef.width = 60;
          canvasRef.height = 60;
        }

        // Clear the canvas context before creating new chart
        ctx.clearRect(0, 0, canvasRef.width, canvasRef.height);

        this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['High', 'Medium', 'Low', 'Out'],
          datasets: [{
            data: [this.highPercent, this.mediumPercent, this.lowPercent, this.outPercent],
            backgroundColor: ['#3EBB80', '#f59e0b', '#FF0000', '#dcdbdb'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          tooltips: {
            enabled: false
          },
          cutoutPercentage: 70
        }
      })
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    },

    updateChart() {
      if (!this.chart) return
      
      this.chart.data.datasets[0].data = [
        this.highPercent, 
        this.mediumPercent, 
        this.lowPercent, 
        this.outPercent
      ]
      this.chart.update()
    },

    showMarkerDetails(item, position) {
      let businesses = []
      if (item.result) {
        let results = item.result
        if (typeof results === 'string') {
          try {
            results = JSON.parse(results)
          } catch (e) {
            console.error('Failed to parse results:', e)
            return
          }
        }
        businesses = Array.isArray(results) ? results.slice(0, 20) : []
      }

      // Add average rank to each business in the popup
      const businessesWithAvgRank = businesses.map((business, index) => {
        // Find the competitor in uniqueCompetitors array to get average rank
        const competitor = this.uniqueCompetitors.find(c => c.place_id === business.place_id)
        return {
          ...business,
          localRank: index + 1, // Rank at this specific location
          avgRank: competitor ? competitor.avgRank : null // Average rank across all locations
        }
      })

      this.selectedMarkerData = {
        coordinates: `${position.lat().toFixed(6)}, ${position.lng().toFixed(6)}`,
        businesses: businessesWithAvgRank
      }
      this.showMarkerPopup = true
    },
    
    closeMarkerPopup() {
      this.showMarkerPopup = false
      this.selectedMarkerData = null
    },

    isMainBusiness(business) {
      const mainBusinessPlaceId = this.businessInfo.place_id || this.businessInfo.google_place_id
      const mainBusinessName = this.businessInfo.business_name
      
      return (mainBusinessPlaceId && business.place_id === mainBusinessPlaceId) || 
             (mainBusinessName && business.name && 
              business.name.toLowerCase() === mainBusinessName.toLowerCase())
    },

    showCompetitorMarker(competitor) {
      // Only show marker if we have valid coordinates and map
      if (!competitor.latitude || !competitor.longitude) {
       
        return
      }

      if (!this.map) {
        console.log('Map not initialized yet');
        return
      }

      this.hideCompetitorMarker()

      const position = new google.maps.LatLng(
        parseFloat(competitor.latitude),
        parseFloat(competitor.longitude)
      )
      
      this.hoverMarker = new google.maps.Marker({
        position: position,
        map: this.map,
        icon: {
          url: this.createCompetitorMarkerSVG(competitor),
          scaledSize: new google.maps.Size(32, 48),
          anchor: new google.maps.Point(16, 48)
        },
        title: `${competitor.name} - Avg Rank #${competitor.avgRank}`,
        zIndex: 9999
      })
    },
    
    hideCompetitorMarker() {
      if (this.hoverMarker) {
        this.hoverMarker.setMap(null)
        this.hoverMarker = null
      }
    },

    switchToCompetitor(competitor) {
      // Store original business info if not already stored
      if (!this.originalBusinessInfo) {
        this.originalBusinessInfo = { ...this.businessInfo };
      }

      // Set the viewing business
      this.viewingBusiness = competitor;

      // Update business info to show competitor's data
      this.businessInfo = {
        name: competitor.name, // Add name field for template
        business_name: competitor.name,
        address: competitor.address,
        place_id: competitor.place_id,
        google_place_id: competitor.place_id,
        rating: competitor.rating,
        reviews: competitor.reviews,
        // Keep original scan data
        createdAt: this.businessInfo.createdAt,
        keywords: this.businessInfo.keywords,
        gridType: this.businessInfo.gridType,
        gridSize: this.businessInfo.gridSize,
        scanArea: this.businessInfo.scanArea,
        scanFrequency: this.businessInfo.scanFrequency,
        grid_size: this.businessInfo.grid_size,
        radius: this.businessInfo.radius,
        radius_unit: this.businessInfo.radius_unit
      };

      // Recalculate stats and markers for this business with selected keyword
      this.recalculateForBusiness(competitor);

      // Close competitors list after selection (both desktop and mobile)
      this.showCompetitors = false;

      // Hide hover marker after selection
      this.hideCompetitorMarker();
    },

    switchBackToMainBusiness() {
      // Restore original business info
      if (this.originalBusinessInfo) {
        this.businessInfo = { ...this.originalBusinessInfo };
      }

      // Clear viewing business
      this.viewingBusiness = null;

      // Recalculate stats and markers for main business
      this.recalculateForBusiness(null);
    },

    recalculateForBusiness(business) {
      // Recalculate ranks for the selected business
      const targetPlaceId = business ? business.place_id :
                           (this.originalBusinessInfo ?
                            (this.originalBusinessInfo.place_id || this.originalBusinessInfo.google_place_id) :
                            (this.businessInfo.place_id || this.businessInfo.google_place_id));

      const targetName = business ? business.name :
                        (this.originalBusinessInfo ? this.originalBusinessInfo.business_name :
                         this.businessInfo.business_name);

      // Update ALL process items with new ranks for the target business
      this.processItems.forEach(item => {
        if (item.result) {
          let results = item.result;
          if (typeof results === 'string') {
            try {
              results = JSON.parse(results);
            } catch (e) {
              item.rank = 0;
              return;
            }
          }

          if (Array.isArray(results)) {
            // Find the rank of the target business
            const businessIndex = results.findIndex(b => {
              const placeId = b.place_id;
              return (targetPlaceId && placeId === targetPlaceId) ||
                     (targetName && b.name && b.name.toLowerCase() === targetName.toLowerCase());
            });

            item.rank = businessIndex >= 0 ? businessIndex + 1 : 0;
          } else {
            item.rank = 0;
          }
        } else {
          item.rank = 0;
        }
      });

      // Re-extract keywords with updated ranks
      this.extractKeywords();

      // Recalculate metrics for the selected keyword
      this.calculateMetrics();

      // Re-extract competitors to update their ranks
      this.extractCompetitors();

      // Update markers on map
      if (this.map) {
        this.updateMapMarkers();
      }

      // Update chart
      if (this.chart) {
        this.updateChart();
      }
    },
    
    createCompetitorMarkerSVG(competitor) {
      const outerColor = competitor.isMainBusiness ? '#22c55e' : '#EA4335' // Green for you, Google red for competitors
      const innerColor = competitor.isMainBusiness ? '#1a9e4a' : '#C5221F' // Darker green for you, darker red for competitors (Google style)

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M16 0C7.16 0 0 7.16 0 16c0 11.2 14.4 30.4 15.36 31.52c0.16 0.16 0.4 0.32 0.64 0.32s0.48-0.16 0.64-0.32C17.6 46.4 32 27.2 32 16c0-8.84-7.16-16-16-16z" fill="${outerColor}" stroke="white" stroke-width="1.5"/>
            <circle cx="16" cy="16" r="7" fill="${innerColor}"/>
          </g>
        </svg>
      `)}`
    },

    toggleKeywords() {
      this.showKeywords = !this.showKeywords
      // Close competitors when opening keywords
      if (this.showKeywords) {
        this.showCompetitors = false
      }
    },

    toggleCompetitors() {
      this.showCompetitors = !this.showCompetitors
    },

    viewReport() {
      const scanId = this.$route.params.id
      if (scanId) {
        this.$router.push({ name: 'recurring-report', params: { id: scanId } })
      }
    },
    
    editScan() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      // Open create-scan page in edit mode in new tab
      const routeData = this.$router.resolve({
        path: '/create-scan',
        query: {
          editId: scanId,
          mode: 'edit'
        }
      });
      window.open(routeData.href, '_blank');
    },
    
    duplicateScan() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      // Open create-scan page in new tab with duplicateFrom parameter
      const routeData = this.$router.resolve({
        path: '/create-scan',
        query: { duplicateFrom: scanId }
      });
      window.open(routeData.href, '_blank');
    },
    
    shareScan() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      if (scanId) {
        this.shareScanId = scanId;
        this.shareScanType = 'grid_scan';
        this.showShareModal = true;
      }
    },

    closeShareModal() {
      this.showShareModal = false;
      this.shareScanId = null;
      this.shareScanType = null;
    },

    onShareCopied() {
      this.$swal({
        icon: 'success',
        title: 'Copied!',
        text: 'Link copied to clipboard',
        timer: 2000,
        showConfirmButton: false
      });
    },
    
    async exportPNG() {
      this.showReportMenu = false;

      let mapContainer = null;
      try {
        this.$swal({
          title: 'Generating PNG...',
          text: 'Please wait while we create your image',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Create a hidden container for the map
        mapContainer = document.createElement('div');
        mapContainer.style.position = 'fixed';
        mapContainer.style.left = '0';
        mapContainer.style.top = '0';
        mapContainer.style.width = '1200px';
        mapContainer.style.height = '800px';
        mapContainer.style.backgroundColor = 'white';
        mapContainer.style.zIndex = '-9999';
        mapContainer.style.visibility = 'hidden';
        document.body.appendChild(mapContainer);

        // Create header section
        const headerSection = document.createElement('div');
        headerSection.style.padding = '20px';
        headerSection.style.backgroundColor = '#f8f9fa';
        headerSection.style.borderBottom = '1px solid #dee2e6';

        const businessName = this.viewingBusiness ?
          this.viewingBusiness.name :
          (this.businessInfo.business_name || this.businessInfo.name || 'Business');

        const address = this.viewingBusiness ?
          this.viewingBusiness.address :
          (this.businessInfo.address || '');

        headerSection.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h2 style="margin: 0; color: #212529; font-size: 24px;">${businessName}</h2>
              <p style="margin: 5px 0 0 0; color: #6c757d; font-size: 14px;">${address}</p>
            </div>
            <div style="text-align: right;">
              <div style="color: #6c757d; font-size: 12px;">Scan Type: Recurring Grid Scan</div>
              <div style="color: #6c757d; font-size: 12px;">Date: ${new Date().toLocaleDateString()}</div>
            </div>
          </div>
        `;
        mapContainer.appendChild(headerSection);

        // Create map div
        const mapDiv = document.createElement('div');
        mapDiv.style.width = '100%';
        mapDiv.style.height = '700px';
        mapContainer.appendChild(mapDiv);

        // Initialize Google Maps - use API key from backend response, store, or env
        const apiKey = this.googleMapsApiKey ||
                      this.$store.getters['apiKeys/googleMapsKey'] ||
                      process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

        if (!apiKey) {
          if (mapContainer) document.body.removeChild(mapContainer);
          throw new Error('Google Maps API key not configured');
        }

        // Make container visible for map initialization
        mapContainer.style.visibility = 'visible';
        mapContainer.style.zIndex = '-1';

        // Create new map instance without controls for clean PNG export
        const map = new google.maps.Map(mapDiv, {
          zoom: this.map.getZoom(),
          center: this.map.getCenter(),
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });

        // Trigger resize to ensure map renders
        google.maps.event.trigger(map, 'resize');

        // Draw grid overlay
        const gridType = this.businessInfo.grid_shape || this.businessInfo.gridType || 'square';

        if (this.processItems.length > 0) {
          const center = this.getGridCenter();

          if (gridType === 'circle' || gridType === 'circular') {
            // Calculate radius
            let maxDistance = 0;
            this.processItems.forEach(item => {
              if (item.latitude && item.longitude) {
                const distance = this.getDistanceFromLatLonInMeters(
                  center.lat, center.lng,
                  parseFloat(item.latitude), parseFloat(item.longitude)
                );
                maxDistance = Math.max(maxDistance, distance);
              }
            });

            const radius = maxDistance * 1.1;

            new google.maps.Circle({
              center: center,
              radius: radius,
              fillColor: '#2477f6',
              fillOpacity: 0.05,
              strokeColor: '#2477f6',
              strokeOpacity: 0.8,
              strokeWeight: 2.5,
              map: map
            });
          } else {
            // Calculate square bounds
            let minLat = Infinity, maxLat = -Infinity;
            let minLng = Infinity, maxLng = -Infinity;

            this.processItems.forEach(item => {
              if (item.latitude && item.longitude) {
                const lat = parseFloat(item.latitude);
                const lng = parseFloat(item.longitude);
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
              }
            });

            const latPadding = (maxLat - minLat) * 0;
            const lngPadding = (maxLng - minLng) * 0;

            new google.maps.Rectangle({
              bounds: {
                north: maxLat + latPadding,
                south: minLat - latPadding,
                east: maxLng + lngPadding,
                west: minLng - lngPadding
              },
              fillColor: '#2477f6',
              fillOpacity: 0.05,
              strokeColor: '#2477f6',
              strokeOpacity: 0.8,
              strokeWeight: 2.5,
              map: map
            });
          }
        }

        // Add markers from current data
        const bounds = new google.maps.LatLngBounds();
        let hasMarkers = false;

        // Add markers for all process items
        if (this.processItems && this.processItems.length > 0) {
          this.processItems.forEach(item => {
            if (item.latitude && item.longitude) {
              const position = new google.maps.LatLng(
                parseFloat(item.latitude),
                parseFloat(item.longitude)
              );

              const rank = item.rank || 0;
              const markerColor = this.getMarkerColor(rank);
              const displayText = rank === 0 || rank > 20 ? '20+' : rank.toString();

              new google.maps.Marker({
                position: position,
                map: map,
                icon: {
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(markerColor, displayText))}`,
                  scaledSize: new google.maps.Size(32, 48),
                  anchor: new google.maps.Point(16, 48)
                }
              });

              bounds.extend(position);
              hasMarkers = true;
            }
          });
        }

        // Fit bounds if we have markers
        if (hasMarkers) {
          map.fitBounds(bounds);
        }

        // Wait for map to load
        await new Promise(resolve => {
          google.maps.event.addListenerOnce(map, 'idle', resolve);
        });

        // Additional wait for tiles to load
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Import html2canvas dynamically
        const html2canvas = (await import('html2canvas')).default;

        // Hide container from view but keep it rendered
        mapContainer.style.position = 'absolute';
        mapContainer.style.left = '-9999px';

        // Capture the container
        const canvas = await html2canvas(mapContainer, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          logging: false,
          backgroundColor: '#ffffff'
        });

        // Remove temporary container
        document.body.removeChild(mapContainer);

        // Convert to blob and download
        canvas.toBlob((blob) => {
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.href = url;

          const cleanBusinessName = businessName.replace(/[^a-z0-9]/gi, '_');
          const dateStr = new Date().toISOString().split('T')[0];

          link.download = `recurring_grid_scan_${cleanBusinessName}_${dateStr}.png`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.$swal({
            icon: 'success',
            title: 'Export Successful',
            text: 'Your PNG has been downloaded',
            timer: 2000,
            showConfirmButton: false
          });
        }, 'image/png');

      } catch (error) {
        console.error('Error exporting PNG:', error);
        this.$swal.close(); // Close the loading dialog first

        // Clean up if error
        if (mapContainer && mapContainer.parentNode) {
          document.body.removeChild(mapContainer);
        }

        setTimeout(() => {
          this.$swal({
            icon: 'error',
            title: 'Export Failed',
            text: error.message || 'Failed to export PNG. Please try again.'
          });
        }, 100);
      }
    },

    getMarkerColor(rank) {
      if (!rank || rank === 0 || rank > 20) return '#dcdbdb';
      if (rank <= 5) return '#3EBB80'; // Green for 1-5
      if (rank <= 14) return '#f59e0b'; // Orange for 6-14
      if (rank <= 20) return '#FF0000'; // Red for 15-20
      return '#dcdbdb';
    },

    // Using the unified createMarkerSVG method above

    async exportCSV() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;

      try {
        // Show loading
        this.$swal({
          title: 'Exporting CSV...',
          text: 'Please wait while we prepare your export',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Use the grid export endpoint for recurring/grid scans
        const exportEndpoint = `${process.env.VUE_APP_API_ENDPOINT}/api/scans/export-grid-csv/${scanId}`;

        // Fetch CSV data
        const response = await axios.get(
          exportEndpoint,
          {
            responseType: 'blob' // Important for file download
          }
        );

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);

        // Get business name from scanData
        const businessName = this.businessInfo.business_name ?
          this.businessInfo.business_name.replace(/[^a-z0-9]/gi, '_') : 'scan';
        const dateStr = new Date().toISOString().split('T')[0];

        link.setAttribute('download', `grid_scan_${businessName}_${dateStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        URL.revokeObjectURL(url);

        // Show success
        this.$swal({
          icon: 'success',
          title: 'Export Successful',
          text: 'Your CSV file has been downloaded',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error exporting CSV:', error);

        // Check if error response is JSON or text
        let errorMessage = 'Failed to export CSV';
        if (error.response) {
          if (error.response.data instanceof Blob) {
            // If response is a blob, try to read it as text
            try {
              const text = await error.response.data.text();
              const jsonError = JSON.parse(text);
              errorMessage = jsonError.message || errorMessage;
            } catch (e) {
              // If can't parse, use default message
            }
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }

        this.$swal({
          icon: 'error',
          title: 'Export Failed',
          text: errorMessage
        });
      }
    },
    
    deleteScan() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      this.deleteIds = [scanId];
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        const response = await axios.delete(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete`,
          {
            data: { scanIds: this.deleteIds }
          }
        );

        if (response.data.success) {
          // Close dialog
          this.showDeleteDialog = false;

          // Show success message
          this.$swal({
            icon: 'success',
            title: 'Deleted!',
            text: response.data.message || 'The scan has been deleted successfully.',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            // Redirect to scans page
            this.$router.push({ name: 'scans' });
          });
        }
      } catch (error) {
        console.error('Error deleting scan:', error);

        // Show error message
        this.$swal({
          icon: 'error',
          title: 'Deletion Failed',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to delete the scan. Please try again.'
        });

        // Close dialog on error too
        this.showDeleteDialog = false;
      } finally {
        this.deleting = false;
      }
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.deleteIds = [];
    }
  }
}
</script>

<style scoped>
/* Breadcrumb strip styles */
.breadcrumb-strip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 16px;
  z-index: 5;
  z-index: 5;  /* Below sidebar overlay but above map */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.breadcrumb-item {
  font-size: 12px;
  background: linear-gradient(90deg, #6b7280 0%, #9ca3af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
}

.breadcrumb-item.clickable {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  transition: all 0.2s;
}

.breadcrumb-item.clickable:hover {
  background: linear-gradient(90deg, #374151 0%, #4b5563 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: underline;
}

.breadcrumb-item.current {
  background: linear-gradient(90deg, #374151 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 6px;
  color: #9ca3af;
  font-size: 14px;
}

/* Import ViewGrid styles - exact same styling */
.view-grid-container {
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.view-grid-container.with-breadcrumb {
  height: calc(100vh - 30px);
  margin-top: 30px;
}

/* Embed container styles */
.embed-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  margin: 0;
  padding: 0;
}

.embed-container .view-grid-container {
  height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0;
}

/* Timeline Navigation */
.timeline-navigation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  z-index: 100;
  height: 60px;
}

.timeline-scroll {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.timeline-track {
  display: flex;
  gap: 0; /* Remove gap to allow precise line positioning */
  align-items: center;
}

.time-range-select {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  font-size: 13px;
  cursor: pointer;
  outline: none;
  margin-left: auto;
}

.time-range-select:focus {
  border-color: #3b82f6;
}

.timeline-point {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.2s;
  min-width: 80px;
  position: relative;
}

/* Connecting line between dots */
.timeline-point:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 14px; /* Position at center of 12px dot (6px from top of dot + 8px padding) */
  left: calc(50% + 10px); /* Start after dot with spacing */
  right: calc(-50% + 10px); /* End before next dot with spacing */
  height: 0.5px; /* Thin line */
  background: #19233A;
  opacity: 0.15;
  z-index: 0;
}

.timeline-point:hover {
  background: #f3f4f6;
}

.timeline-point.active {
  background: transparent;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: white;
  border: 1px solid #19233A;
  transition: all 0.2s;
  position: relative;
  z-index: 2; /* Higher than line */
}

.timeline-point:hover .timeline-dot {
  transform: scale(1.1);
  border-width: 2px;
}

.timeline-point.active .timeline-dot {
  background: #3EBB80;
  border: 2px solid #3EBB80;
  width: 14px;
  height: 14px;
  box-shadow: 0 0 0 3px rgba(62, 187, 128, 0.2);
}

.timeline-label {
  font-size: 11px;
  color: #19233A;
  white-space: nowrap;
  text-align: center;
  line-height: 1.2;
}

.timeline-point.active .timeline-label {
  color: #19233A;
  font-weight: 600;
}

/* Full page map - adjusted for timeline */
.google-map-full {
  position: absolute;
  top: 60px; /* Space for timeline */
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: calc(100% - 60px);
  transition: top 0.3s ease, height 0.3s ease;
}

/* Adjust map when timeline is hidden */
.google-map-full.timeline-hidden {
  top: 0;
  height: 100%;
}

/* Slide up animation for timeline */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave {
  transform: translateY(0);
  opacity: 1;
}

/* Loading and No Data states */
.loading-container, .no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  background: #f9fafb;
}

.loading-spinner {
  margin-bottom: 20px;
  display:flex;
  justify-content: center;
}

.spinner {
  animation: rotate 2s linear infinite;
}

.spinner .path {
  stroke: #3b82f6;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.no-data-card {
  background: white;
  border-radius: 12px;
  padding: 48px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-width: 500px;
}

.no-data-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.no-data-card h2 {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.no-data-card p {
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.6;
}

.schedule-info {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
}

.schedule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.schedule-item:last-child {
  margin-bottom: 0;
}

.schedule-label {
  font-weight: 500;
  color: #374151;
}

.schedule-value {
  color: #6b7280;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.dashboard-btn {
  background: #3b82f6;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dashboard-btn:hover {
  background: #2563eb;
}

/* Scan status overlay */
.scan-status-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
}

.status-card {
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  text-align: center;
  min-width: 300px;
}

.status-card h3 {
  margin: 0 0 8px 0;
  color: #1f2937;
}

.status-card p {
  color: #6b7280;
  margin: 0 0 16px 0;
}

.progress-info {
  font-size: 14px;
  color: #3b82f6;
  font-weight: 500;
}

/* Loading overlay for execution change */
.execution-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.execution-loading-overlay .loading-content {
  background: white;
  padding: 24px 32px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.execution-loading-overlay .loading-spinner {
  margin: 0 auto 16px auto;
}

.execution-loading-overlay p {
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
}

/* Left sidebar - matching ViewGrid exactly */
.sidebar-overlay {
  position: absolute;
  top: 70px; /* Adjusted for timeline */
  left: 20px;
  width: 420px; /* Increased width */
  max-height: calc(100vh - 90px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 10;
  transition: top 0.3s ease;
}

/* Adjust sidebar for embed mode */
.embed-container:not(.with-breadcrumb) .sidebar-overlay {
  top: 70px;
  max-height: calc(100vh - 90px);
}

/* Adjust sidebar when timeline is hidden (no breadcrumb) */
.view-grid-container:has(.timeline-navigation[style*="display: none"]) .sidebar-overlay {
  top: 20px;
  max-height: calc(100vh - 30px);
}

/* Simple adjustment for embedded mode with breadcrumb:
   Add 30px to all top positions */

/* Adjust sidebar when breadcrumb is shown - MUST override other rules */
.view-grid-container.with-breadcrumb .sidebar-overlay,
.embed-container.with-breadcrumb .sidebar-overlay {
  top: 100px !important;  /* 70px original + 30px for breadcrumb */
  max-height: calc(100vh - 120px) !important;
}

/* Adjust timeline when breadcrumb is shown */
.view-grid-container.with-breadcrumb .timeline-navigation {
  top: 30px !important;  /* 0px original + 30px for breadcrumb */
}

/* Adjust top controls when breadcrumb is shown - MUST override other rules */
.view-grid-container.with-breadcrumb .top-controls {
  top: 100px !important;  /* 70px original + 30px for breadcrumb */
}

/* Custom Scrollbar */
.sidebar-overlay::-webkit-scrollbar {
  width: 6px;
}

.sidebar-overlay::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-overlay::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sidebar-overlay::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Business Card Block - matching ViewGrid */
.business-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Business Section */
.business-section {
  display: flex;
  gap: 16px;
}

.business-info {
  flex: 1;
}

.business-name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4b5563;
  line-height: 1.5;
}

.info-row i {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
  width: 14px;
  flex-shrink: 0;
}

/* Average Section */
.average-section {
  padding: 12px 16px;
  background: #3EBB8026;
  border-radius: 6px;
  text-align: center;
  min-width: 80px;
  align-self: flex-start;
}

.average-section .section-label {
  font-size: 8px;
  color: #00000080;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-weight: 600;
}

.average-value {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

/* Keywords Header */
.keywords-header {
  padding: 7px 20px;
  background: white;
  /* border-bottom: 1px solid #e5e7eb; */
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
}

.keywords-header:hover {
  background: #f9fafb;
}

.keywords-list {
  padding: 16px 0px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideDown 0.3s ease;
  max-height: 300px;
  overflow-y: auto;
}

.keyword-item {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.keyword-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
  transform: translateY(-1px);
}

.keyword-item.active {
  border-color: #3b82f6;
  background: #eff6ff;
}

.keyword-name {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
}

.keyword-item.active .keyword-name {
  color: #3b82f6;
  font-weight: 600;
}

.keyword-rank {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.keyword-item.active .keyword-rank {
  color: #3b82f6;
  font-weight: 600;
}

/* Chart Section - Inside Business Card - matching ViewGrid */
.chart-section-inner {
  padding: 16px;
  background: white;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #e5e7eb;
  border-top: 1px solid #e5e7eb;
}

/* Keywords Section - Inside Business Card */
/* .keywords-section {
  padding: 12px 0;
} */

.chart-section-inner .chart-container {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
}

.chart-section-inner .stats-row {
  display: flex;
  flex: 1;
  gap: 16px;
}

.chart-container canvas {
  width: 100% !important;
  height: 100% !important;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 9px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Competitors Section - matching ViewGrid */
.competitors-section {
  position: fixed;
  bottom: 0px;
  left: 80px; /* Positioned at the left edge after sidebar */
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 420px;
  transition: all 0.3s ease;
}

/* Adjust competitors section for embed mode */
.embed-container .competitors-section {
  left: 20px; /* Align with sidebar in embed mode */
  bottom: 0px;
}

/* Adjust position for public views without sidebar */
.competitors-section.public-view {
  left: 20px;
}

.competitors-header {
  padding: 14px 20px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
  border-radius: 12px 12px 0 0;
}

.competitors-header:hover {
  background: #f9fafb;
}

.competitors-list {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
  animation: slideUp 0.3s ease;
  overflow-y: auto;
  background: white;
  border-radius: 0 0 12px 12px;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.competitors-section.expanded {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.competitor-card {
  padding: 12px 16px;
  margin: 0 -16px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.competitor-card:last-child {
  border-bottom: none;
}

.competitor-card:hover {
  background: #f9fafb;
}

.competitor-card.active-business {
  background: #fef3c7 !important;
  border-bottom-color: #f59e0b !important;
}

/* Viewing Banner */
/* Back to Business Button - Floating */
.back-to-business-btn {
  position: absolute;
  left: 450px; /* Position to the right of sidebar */
  background: white;
  color: #19233A;
  border: none;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 11;
}

.back-to-business-btn:hover {
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.competitor-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
}

.competitor-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  flex: 1;
}

.you-label {
  color: #3b82f6;
  font-size: 12px;
  font-weight: 500;
}

.competitor-rank {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 15px;
  color: white;
  white-space: nowrap;
}

.competitor-rank.rank-high { background: #10b981; }
.competitor-rank.rank-medium { background: #f59e0b; }
.competitor-rank.rank-low { background: #ef4444; }

.competitor-address {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
  line-height: 1.3;
}

.competitor-rating {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.stars {
  display: flex;
  gap: 1px;
}

.stars i {
  font-size: 12px;
  color: #e5e7eb;
}

.stars i.filled {
  color: #fbbf24;
}

.rating-value {
  font-weight: 600;
  color: #1f2937;
}

.review-count {
  color: #6b7280;
}

/* Execution loading indicator */

/* Top Controls Container */
.top-controls {
  position: absolute;
  top: 70px; /* Added spacing from timeline */
  right: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
  z-index: 10;
  transition: top 0.3s ease;
}

/* Move controls up when timeline is being hidden */
.view-grid-container:has(.slide-up-leave-active) .top-controls {
  top: 20px;
}

/* Keep controls up when timeline is hidden */
.view-grid-container:has(.slide-up-leave-to) .top-controls {
  top: 20px;
}

/* Report Button Container */
.report-button-container {
  position: relative;
  display: flex;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 5px;
}

/* VIEW REPORT Button */
.view-report-btn {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px 0 0 6px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.view-report-btn:hover {
  background: #4338ca;
}

/* Menu Dots Button */
.menu-dots-btn {
  background: white;
  border: none;
  padding: 10px 12px;
  border-radius: 0 6px 6px 0;
  border-left: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
}

.menu-dots-btn:hover {
  background: #f9fafb;
  color: #374151;
}

/* Report Dropdown Menu */
.report-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 100;
  overflow: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s;
  color: #000000;
  font-size: 13px;
  font-weight: 500;
}

.menu-item:hover {
  background: #f3f4f6;
}

.menu-item.delete {
  color: #ef4444;
  border-top: 1px solid #e5e7eb;
}

.menu-item.delete:hover {
  background: #fef2f2;
}

.menu-item i {
  width: 16px;
  font-size: 14px;
  opacity: 0.7;
  margin-right: 2px;
}

/* Timeline Toggle Button */
.timeline-toggle-btn {
  background: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;
  color: #6b7280;
}

.timeline-toggle-btn:hover {
  background: #f9fafb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Popup Overlay */
.popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 19;
}

/* Marker Popup - matching ViewGrid */
.marker-popup {
  position: absolute;
  top: 70px; /* Below timeline */
  right: 20px;
  width: 500px;
  max-height: calc(100vh - 90px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  z-index: 20;
  display: flex;
  flex-direction: column;
}


.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.popup-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #111827;
}

.popup-coordinates {
  padding: 8px 20px;
  font-size: 12px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.popup-businesses {
  overflow-y: auto;
  max-height: calc(100vh - 160px);
  padding: 12px;
}

.business-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}

.business-item:hover {
  background: #f9fafb;
}

.business-item:last-child {
  border-bottom: none;
}

.business-rank-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.business-rank-badge.rank-top {
  background: #dcfce7;
  color: #15803d;
}

.business-rank-badge.rank-high {
  background: #dbeafe;
  color: #1e40af;
}

.business-rank-badge.rank-medium {
  background: #fed7aa;
  color: #c2410c;
}

.business-rank-badge.rank-low {
  background: #fee2e2;
  color: #b91c1c;
}

.business-details {
  flex: 1;
  min-width: 0;
}

.business-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.business-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.3;
}

.you-badge {
  display: inline-block;
  background: #3b82f6;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  font-weight: 500;
}

.rank-display {
  font-size: 13px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 15px;
}

.rank-display.rank-top {
  color: #15803d;
  background: #dcfce7;
}

.rank-display.rank-high {
  color: #1e40af;
  background: #dbeafe;
}

.rank-display.rank-medium {
  color: #c2410c;
  background: #fed7aa;
}

.rank-display.rank-low {
  color: #b91c1c;
  background: #fee2e2;
}

.business-address {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
  line-height: 1.4;
}

.business-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rating-section {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.rating {
  font-weight: 600;
  color: #111827;
}

.stars {
  display: flex;
  gap: 1px;
}

.stars i {
  font-size: 10px;
  color: #e5e7eb;
}

.stars i.filled {
  color: #fbbf24;
}

.reviews {
  color: #6b7280;
}

.scan-btn {
  background: white;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.scan-btn:hover {
  background: #3b82f6;
  color: white;
}

/* Mobile Styles */
/* Hide mobile elements by default on desktop */
.mobile-only {
  display: none;
}

.mobile-toggle-btn {
  display: none;
}

.mobile-menu-dots {
  display: none;
}

.mobile-drag-handle {
  display: none;
}

/* Mobile Media Query */
@media (max-width: 768px) {
  /* Hide desktop, show mobile */
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  /* Fix viewport to prevent scrolling */
  body {
    overflow-x: hidden !important;
  }

  .view-grid-container {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
  }

  /* Sidebar positioned at top on mobile */
  .sidebar-overlay {
    position: fixed !important;
    top: 60px !important; /* Below mobile header */
    left: 10px !important;
    right: 10px !important;
    width: calc(100% - 20px) !important;
    height: auto !important;
    z-index: 100 !important;
    background: transparent;
  }

  /* Business card adjustments for mobile */
  .business-card {
    width: 100% !important;
    height: auto !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden !important;
    background: white;
    margin-top: 10px !important;
    padding: 0px;
  }

  /* Business section takes full width */
  .business-section {
    display: block !important;
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    gap: 0 !important;
    border-bottom: none !important;
  }

  /* Business header takes full width edge to edge */
  .business-header-with-toggle {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    padding: 12px 16px !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    background: white !important;
  }

  .business-header-with-toggle .business-name {
    font-size: 16px !important;
    flex: 1 !important;
    margin: 0 !important;
  }

  /* Mobile actions container */
  .mobile-actions {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
  }

  /* Mobile toggle button */
  .mobile-toggle-btn {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 32px !important;
    height: 32px !important;
    border: none !important;
    background: #f3f4f6 !important;
    border-radius: 8px !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
  }

  .mobile-toggle-btn:hover {
    background: #e5e7eb !important;
  }

  .mobile-toggle-btn i {
    font-size: 14px !important;
    color: #6b7280 !important;
  }

  /* Mobile menu dots button */
  .mobile-menu-dots {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 32px !important;
    height: 32px !important;
    border: none !important;
    background: transparent !important;
    cursor: pointer !important;
  }

  .mobile-menu-dots i {
    font-size: 16px !important;
    color: #6b7280 !important;
  }

  /* Mobile business content */
  .mobile-business-content {
    width: 100% !important;
    animation: slideDown 0.3s ease;
  }

  .mobile-business-content .business-info {
    padding: 12px 16px !important;
  }

  /* Mobile average section */
  .mobile-average-section {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 16px !important;
    background: #3EBB8026 !important;
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    border-radius: 6px;
  }

  .mobile-average-section .section-label {
    font-size: 10px !important;
    color: #6b7280 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 0 !important;
    font-weight: 600 !important;
  }

  .mobile-average-section .average-value {
    font-size: 28px !important;
    font-weight: 700 !important;
    color: #111827 !important;
  }

  /* Mobile Back to Business Button */
  .mobile-back-button {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    width: 100% !important;
    padding: 12px 16px !important;
    background: #EFF6FF !important;
    border: none !important;
    border-bottom: 1px solid #DBEAFE !important;
    color: #2477F6 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: background 0.2s !important;
    text-align: left !important;
  }

  .mobile-back-button:hover {
    background: #DBEAFE !important;
  }

  .mobile-back-button i {
    font-size: 14px !important;
  }

  /* Competitors section on mobile */
  .competitors-section {
    position: fixed !important;
    bottom: 0px !important;
    left: 10px !important;
    right: 10px !important;
    width: calc(100% - 20px) !important;
  }

  /* Mobile Keyword Section */
  .mobile-keyword-section {
    margin: 0 !important;
    background: white !important;
    border-top: 1px solid #e5e7eb !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .mobile-keyword-section .keywords-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 16px !important;
    cursor: pointer !important;
  }

  .mobile-keyword-section .section-label {
    font-size: 10px !important;
    color: #6b7280 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    font-weight: 600 !important;
  }

  .mobile-keywords-list {
    padding: 0 16px 16px 16px !important;
    max-height: 200px !important;
    overflow-y: auto !important;
  }

  .mobile-keyword-item {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 8px 12px !important;
    margin-bottom: 4px !important;
    background: #f9fafb !important;
    border-radius: 6px !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
  }

  .mobile-keyword-item:hover {
    background: #e5e7eb !important;
  }

  .mobile-keyword-item.active {
    border: 1px solid #3b82f6 !important;
    background: #eff6ff !important;
  }

  .mobile-keyword-item .keyword-name {
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  .mobile-keyword-item .keyword-rank {
    font-size: 11px !important;
    color: #6b7280 !important;
  }

  .mobile-keyword-item.active .keyword-rank {
    color: rgba(255, 255, 255, 0.8) !important;
  }

  /* Mobile Chart Section */
  .mobile-chart-section {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 16px !important;
    padding: 16px !important;
    background: white !important;
    border-top: 1px solid #e5e7eb !important;
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .mobile-chart-section .chart-container {
    width: 60px !important;
    height: 60px !important;
    flex-shrink: 0 !important;
  }

  .mobile-chart-section .chart-container canvas {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
  }

  .mobile-chart-section .stats-row {
    display: flex !important;
    flex: 1 !important;
    gap: 12px !important;
  }

  .mobile-chart-section .stat-item {
    flex: 1 !important;
    text-align: center !important;
  }

  .mobile-chart-section .stat-value {
    font-size: 14px !important;
    font-weight: 700 !important;
    margin-bottom: 2px !important;
  }

  .mobile-chart-section .stat-label {
    font-size: 9px !important;
    color: #6b7280 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px !important;
  }

  /* Hide desktop chart section on mobile */
  .chart-section-inner {
    display: none !important;
  }

  /* Hide desktop view report button on mobile */
  .report-button-container {
    display: none !important;
  }

  /* Hide floating back to business button on mobile */
  .back-to-business-btn {
    display: none !important;
  }

  /* Marker popup responsive - slide up from bottom */
  .marker-popup {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 70vh !important;
    z-index: 100;
    animation: slideUpFromBottom 0.3s ease !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2) !important;
  }

  /* Mobile drag handle - visible on mobile */
  .mobile-drag-handle {
    display: flex !important;
    justify-content: center !important;
    padding: 8px 0 4px 0 !important;
    cursor: grab !important;
  }

  .mobile-drag-handle span {
    width: 40px !important;
    height: 4px !important;
    background: #d1d5db !important;
    border-radius: 2px !important;
  }

  /* Adjust popup header for mobile */
  .popup-header {
    padding: 12px 16px !important;
    border-bottom: 1px solid #e5e7eb !important;
  }

  .popup-header h3 {
    font-size: 16px !important;
  }

  /* Adjust popup content for mobile */
  .popup-businesses {
    padding: 12px !important;
  }

  .popup-business-card {
    padding: 10px !important;
    margin-bottom: 10px !important;
  }

  /* Timeline navigation on mobile */
  .timeline-navigation {
    height: 50px !important;
  }

  /* Map adjustments for mobile */
  .google-map-full {
    top: 50px !important; /* Account for timeline */
  }

  .google-map-full.timeline-hidden {
    top: 0 !important;
  }

  /* Top controls on mobile */
  .top-controls {
    top: 70px !important;
    right: 10px !important;
  }

  /* Animations */
  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 500px;
    }
  }

  @keyframes slideUpFromBottom {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  /* Info row adjustments */
  .info-row {
    font-size: 12px;
    padding: 4px 0;
  }

  .info-row i {
    width: 16px;
    font-size: 12px;
  }
}
</style>
<!-- Global styles to remove padding from parent col class -->
<style>
/* Remove padding from parent col class for this view only */
.col {
  padding: 0 !important;
}

/* Also ensure fill-height has no padding */
.fill-height {
  padding: 0 !important;
  margin: 0 !important;
}

/* Remove any container padding as well */
.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}
</style>